import produce, {Draft} from 'immer';

import {assert} from 'Asserts';
import {DEFAULT_DEFAULT_VALUE} from 'notebook/data-tables/DataTableConstants';
import {StoreDependencyMap} from 'notebook/mediocre/EditorTypes';
import {
  RegistrationTable,
  RegistrationTableBase,
  RegistrationTableConfig,
} from 'notebook/registration-tables/RegistrationTableConstants';
import {
  createTable,
  getColumnDefinitionsFromStructuredTableColumns,
  getDefaultRows,
} from 'notebook/registration-tables/RegistrationTableUtil';
import {getColumnDefinitionIndex} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {LegacyStructuredTableRow} from 'notebook/structured-tables/StructuredTableConstants';
import {CELL_UI_PROPERTIES} from 'notebook/tables/constants/CellConstants';
import {RegistrationTableMetadata, Table, TableColumnType, TableData} from 'notebook/tables/TableConstants';
import {getBioentityInfoFromTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {omit} from 'util/omitKey';
import {StageRun} from 'workflows/WorkflowConstants';

function convertRegistrationTableOrConfigToTable(
  registrationTable: RegistrationTableConfig | RegistrationTable,
  fileId: string,
  stageRuns: Array<StageRun>
): Table {
  let rows = registrationTable.rows;
  const columns = registrationTable.columns;

  if (rows == null || rows.length === 0) {
    rows = getDefaultRows(fileId, columns, stageRuns).map((cells) => ({isFinalized: false, cells}));
  }
  return createTable({
    registrationTable: {
      ...registrationTable,
      columns,
      rows,
    },
    stageRuns,
  });
}

export function convertRegistrationTableToTable(
  registrationTable: RegistrationTable,
  fileId: string,
  stageRuns: Array<StageRun>
): Table {
  return convertRegistrationTableOrConfigToTable(registrationTable, fileId, stageRuns);
}

export function convertRegistrationTableConfigToTable(
  registrationTable: RegistrationTableConfig,
  fileId: string
): Table {
  return convertRegistrationTableOrConfigToTable(registrationTable, fileId, []);
}

function updateTableDraft(
  draftRegistrationTable: Draft<RegistrationTableBase>,
  table: TableData,
  forConfig: boolean
): void {
  assert(table.rowData != null, 'Tried to update a registration table with an empty rowData', {
    table,
  });
  assert(
    table.rowData.length === table.table.length,
    `Table row data and table row count don't match: ${table.rowData.length} != ${table.table.length}`
  );

  let structuredTableColumns = table.columnConstraints.map((columnConstraint, index) => ({
    ...columnConstraint!.structuredTableColumn!,
    width: table.widths[index],
  }));
  let rows: Array<LegacyStructuredTableRow>;

  if (table.rowData.length === 1 && forConfig) {
    // RegistrationTableConfig components cannot have a single row. If we get a 1-row table, convert it
    // to a zero-row table, and move the values into the column defaults
    rows = [];

    const firstRow = table.table[0];
    assert(firstRow && firstRow.length === structuredTableColumns.length, 'Table must have one complete row');
    structuredTableColumns = structuredTableColumns.map((column, colIndex) => ({
      ...column,
      defaultValue: omit(firstRow[colIndex], ...CELL_UI_PROPERTIES),
    }));
  } else {
    // Sanitize the cell values
    rows = table.rowData.map((rowData, rowIndex) => ({
      ...rowData,
      cells: table.table[rowIndex].map((cell) => omit(cell, ...CELL_UI_PROPERTIES)),
    })) as Array<LegacyStructuredTableRow>;

    // Clear out the default values in columns for config tables with > 1 row
    if (forConfig) {
      structuredTableColumns = structuredTableColumns.map((column) => ({
        ...column,
        defaultValue: DEFAULT_DEFAULT_VALUE,
      }));
    }
  }

  draftRegistrationTable.rows = rows;
  draftRegistrationTable.columns = structuredTableColumns;
  draftRegistrationTable.name = table.name;

  const labelingStrategy = table.metadata && (table.metadata as RegistrationTableMetadata).labelingStrategy;
  if (labelingStrategy) {
    draftRegistrationTable.labelingStrategy = labelingStrategy;
  }
}

export function mergeTableUpdateIntoRegistrationTable(
  registrationTable: RegistrationTable,
  table: TableData
): RegistrationTable {
  return produce(registrationTable, (draftRegistrationTable) => {
    updateTableDraft(draftRegistrationTable, table, false);
  });
}

export function mergeTableUpdateIntoRegistrationTableConfig(
  registrationTableConfig: RegistrationTableConfig,
  table: TableData
): RegistrationTableConfig {
  return produce(registrationTableConfig, (draftRegistrationTable) => {
    updateTableDraft(draftRegistrationTable, table, true);
  });
}

export function getStoreDependenciesForRegistrationTable(
  registrationTable: RegistrationTable
): StoreDependencyMap {
  if (!registrationTable.tagSchema) {
    return {};
  }
  const bioentityDependencies: Array<string> = [];
  const editableRows = registrationTable.rows.filter((row) => !row.isFinalized).map((row) => row.cells);
  const columns = registrationTable.columns;
  const columnDefinitions = getColumnDefinitionsFromStructuredTableColumns(columns, {
    tagSchema: registrationTable.tagSchema,
    batchSchema: registrationTable.batchSchema,
    stageRuns: [],
  });
  const existingBioentityColumnIndex = getColumnDefinitionIndex({
    columnDefinitionType: TableColumnType.EXISTING_BIOENTITY,
    tableColumnDefinitions: columnDefinitions,
  });
  for (const row of editableRows) {
    const bioentityInfo = getBioentityInfoFromTypedValue(row[existingBioentityColumnIndex].typedValue);
    if (bioentityInfo) {
      bioentityDependencies.push(bioentityInfo.id);
    }
  }
  return {bioentityIds: bioentityDependencies};
}
